import {put, call, all} from 'redux-saga/effects';
import Answer from "../../entities/answer";
import * as Actions from "../actions/answerActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type AnswerAction = {
    answerId: string;
    answer: Answer;
    answers: Answer[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneAnswer(action: AnswerAction) {
	const session = tabSession.captureGeneration();
	try {
		const answer: Answer = yield call(api.findOne, 'answer', action.answerId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_FETCH_ONE_SUCCESS, answerId: action.answerId, answer, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_FETCH_ONE_FAILURE, answerId: action.answerId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {AnswerAction} action
 */
export function* saveAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ANSWER_SAVE saga', action.answer?.id);

		if (action.answer === undefined || action.answer === null) {
			console.error('saveAnswer received a null');
			throw new Error('saveAnswer received a null');
		}

		if (!(action.answer instanceof Answer)) {
			action.answer = new Answer(action.answer);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.answer.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.answer);
			const addResult = yield call(idb.add, 'answer', action.answer);
			if (addResult.isErr()) {
				console.error(`⮂ saveAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertAnswerSave(action.answer, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ANSWER_SAVE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ANSWER_SAVE, answer: action.answer, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertAnswerSave(action.answer, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ANSWER_SAVE_FAILURE, answer: action.answer, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {AnswerAction} action
 * @param {Answer[]} action.answers
 */
export function* saveManyAnswers(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ANSWER_SAVE_MANY saga', action.answers.map(answer => answer.id).join(','));

        if (!Array.isArray(action.answers)) {
            console.error('saveManyAnswers expected an array, received', action.answers);
            throw new Error('saveManyAnswers expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveAnswer), under these queue keys
		const dispatchedAddedOns: number[] = action.answers.map(answer => answer?.addedOn);
		const revertAll = () => action.answers.map((answer, i) =>
			put(Actions.revertAnswerSave(answer, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Answer[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.answers.length; i++) {
				const answer = action.answers[i];
				answer.persisted = false;
				ensureOperationId(answer);
				toSave.push(answer);
			}

			const addResult = yield call(idb.add, 'answer', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyAnswers: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ANSWER_SAVE_MANY_FAILURE, answers: action.answers, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ANSWER_SAVE_MANY, answers: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ANSWER_SAVE_MANY_FAILURE, answers: action.answers, status: 500, err});

			console.error(`Error saving ${action.answers.length} answers to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {AnswerAction} action
 */
export function* removeAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.answer === undefined || action.answer === null || !(action.answer instanceof Answer)) {
			console.error('removeAnswer expected type Answer, received', action.answer);
			reportQueued(false);
			throw new Error('removeAnswer expected type Answer');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.answer.status = 'D';
		action.answer.addedOn = newHRTimestamp();
		action.answer.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.answer);
		const addResult = yield call(idb.add, 'answer', action.answer);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ANSWER_REMOVE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_REMOVE, answer: action.answer});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_REMOVE_FAILURE, answer: action.answer, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Answer.
	*
	* @param {AnswerAction} action
	*/
export function* restoreAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreAnswer expected getCurrentUserId() to return a value');
        return;
    }

	if (action.answer === undefined || action.answer === null) {
        console.error('restoreAnswer expected a Answer, received null');
        throw new Error('restoreAnswer expected a Answer, received null');
    }
    else if (!(action.answer instanceof Answer)) action.answer = new Answer(action.answer); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.answer.status = 'U';
		action.answer.addedOn = newHRTimestamp();
		action.answer.addedBy = userId;
		action.answer.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.answer);
		const addResult = yield call(idb.add, 'answer', action.answer);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ANSWER_RESTORE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_RESTORE, answer: action.answer});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_RESTORE_FAILURE, answer: action.answer, status: 500, err});

		console.error('Error restoring from database', err);
	}
}